import * as opt from "lib/opt";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import {
	DefaultValues,
	FieldValues,
	RegisterOptions,
	useForm,
	UseFormReturn,
	useWatch,
} from "react-hook-form";
import { InitialFormData } from "store";
import { FormSlice } from "store/slices/formSlice";
import * as util from "lib/util";
import * as hooks from "lib/hooks";
import * as result from "lib/result";
import { alpha, TextFieldProps } from "@mui/material";
import { FileData } from "lib/util";
import { useParams, useLocation } from "react-router-dom";
import { UploadAction } from "enums/uploadMode";

export function UseAppForm() {
	const [activeStep, setActiveStep] = useState(0);
	const [stepStatus, setStepStatus] = useState(
		result.Ok<{ formProgress: "pending" | "completed" }>({
			formProgress: "pending",
		})
	);
	const f = useForm<FileData>({
		defaultValues: InitialFormData,
		mode: "onChange",
	});

	const resetStepStatus = useCallback(() => {
		setStepStatus(result.Ok({ formProgress: "pending" }));
	}, [setStepStatus]);

	const { handleNewFileAction } = hooks.useFileUploadHandler({
		form: f,
		setActiveStep,
		setStepStatus,
		resetStepStatus,
	});

	const resetUploadPage = useCallback(() => {
		f.reset();
		resetStepStatus();
		setActiveStep(0);
	}, [f, resetStepStatus]);

	const formNull = useWatch<FileData>({
		control: f.control,
	}) as FileData | null;

	const formState = opt.FromNullable(formNull);

	const reset = useCallback(() => {
		f.reset(InitialFormData);
		setActiveStep(0);
		resetStepStatus();
	}, [f, resetStepStatus]);

	const { theme } = hooks.UseTheme();

	const getTextFieldProps = useCallback(
		(
			label: string,
			key: keyof FileData,
			opts?: RegisterOptions<FileData, keyof FileData> & {
				cb?: (e: any) => void;
			}
		): Partial<TextFieldProps> => {
			const registerOpts = (opts || {}).Omit("cb") as RegisterOptions<
				FileData,
				keyof FileData
			>;
			return {
				...f.register(key, { required: true, ...registerOpts }),
				onChange: (e) => {
					f.setValue(key, e.target.value);
					f.trigger(key);
					opts?.cb?.(e);
				},
				label,
				fullWidth: true,
				variant: "outlined",
				error: !!f.formState.errors[key],
				sx: {
					"& .MuiOutlinedInput-root": {
						"borderRadius": 2,
						"backgroundColor": alpha(
							theme.palette.background.paper,
							0.6
						),
						"boxShadow": `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
						"transition": "all 0.2s ease",
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: alpha(theme.palette.primary.main, 0.6),
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderWidth: 2,
							borderColor: theme.palette.primary.main,
						},
					},
					"& .MuiInputLabel-root": {
						fontWeight: 500,
					},
					"& input": {
						padding: "14px 16px",
					},
				},
			};
		},
		[f, theme]
	);

	const formValidator = util.GetFormValidator(f);
	const defaultValidationKeys: (keyof FileData)[] = [
		"file",
		"parsed",
		"containmentName",
		"description",
		"plants",
	];

	const isFormValid = (
		keysToCheck: (keyof FileData)[] = defaultValidationKeys
	) => formValidator(keysToCheck);

	return {
		f,
		isFormValid,
		setStepStatus,
		activeStep,
		setActiveStep,
		stepStatus,
		resetStepStatus,
		formActions: {
			newFile: (files: File[]) =>
				handleNewFileAction(FormSlice.actions.newFile(files)),
		},
		loading: formState.Map((f) => f.loading).UnwrapOr(false),
		formState,
		hasUnsavedChanges: () => {
			// Use react-hook-form's built-in dirty tracking
			return f.formState.isDirty;
		},
		reset,
		resetUploadPage,
		getTextFieldProps,
	};
}

export const FormCtx = createContext<ReturnType<typeof UseAppForm>>(
	{} as ReturnType<typeof UseAppForm>
);

export function FormProvider({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element {
	const form = UseAppForm();
	return <FormCtx.Provider value={form}>{children}</FormCtx.Provider>;
}

export function UseFormContext(): ReturnType<typeof UseAppForm> {
	return useContext(FormCtx);
}

type addSerialModeResult =
	| { addSerialMode: true; id: string }
	| { addSerialMode: false; id: undefined };

export function UseAddSerialMode(): addSerialModeResult & {
	lockMode: boolean;
	unlockMode: boolean;
	action: UploadAction | undefined;
	byLpn?: boolean;
} {
	const { id } = useParams();
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const actionParam = params.get("action");
	const action =
		actionParam === UploadAction.Lock || actionParam === UploadAction.Unlock
			? actionParam
			: undefined;
	const byLpnParam = params.get("byLpn");
	const byLpn = byLpnParam !== null ? byLpnParam === "true" : undefined;
	const lockMode = !!id && action === UploadAction.Lock;
	const unlockMode = !!id && action === UploadAction.Unlock;

	if (!isNil(id)) {
		return {
			addSerialMode: true,
			lockMode,
			unlockMode,
			action,
			byLpn,
			id: id,
		};
	}

	return {
		addSerialMode: false,
		lockMode: false,
		unlockMode: false,
		action,
		byLpn: undefined,
		id: undefined,
	};
}

/**
 * Generic hook to track form for editing existing values
 *
 */
export function UseEditingForm<T, TDTO extends FieldValues = T & FieldValues>(
	originalData: T,
	opts: {
		toDTO: (data: T) => TDTO;
		compareFunction?: (a: TDTO, b: TDTO) => bool;
	}
) {
	const [currentData, setCurrentData] = useState(originalData);

	const f = useForm<TDTO>({
		defaultValues: opts.toDTO(currentData) as DefaultValues<TDTO>,
		mode: "onChange",
	});

	const [isDirty, setIsDirty] = useState(false);

	const reset = (newData?: T) => {
		// If new value is provided, update our state
		if (newData) {
			setCurrentData(newData);
			// Reset form with the new values
			f.reset(opts.toDTO(newData));
		} else {
			// Just reset to current value if no new one provided
			f.reset(opts.toDTO(currentData));
		}
		// Reset dirty state
		setIsDirty(false);
	};

	// Watch form values for comparison
	const p = useWatch<TDTO>({
		control: f.control,
	});

	// Update dirty state when form values change
	useEffect(() => {
		if (!p) return; // Skip if form values not loaded yet

		const currentDTO = opts.toDTO(currentData);		
		const result = !currentDTO.Entries().All(([key, val]) => {
			const formValue = p.Get<TDTO, keyof TDTO>(key);
			const hasValue = formValue.IsSome();
			
			// Skip fields not in the form (e.g., sector, sectorType - read-only fields)
			if (!hasValue) {
				return true; // Don't mark as dirty for missing fields
			}
			
			const v = formValue.Unwrap();
			const isEqual = opts.compareFunction
				? opts.compareFunction(v as any, val as any)
				: util.AreEqual(v, val);
			
			return isEqual;
		});	

		setIsDirty(result);
	}, [p, currentData]);

	return {
		isDirty,
		reset,
		f,
	};
}
